import { AxiosPromise } from 'axios';
import { http } from 'services';
import type { BankQuestion } from './types';

// Optional, type-specific fields kept in sync with the shared BankQuestion shape.
type BankQuestionTypeFields = Partial<
  Pick<
    BankQuestion,
    | 'blanks' | 'reorderItems' | 'correctOrder'
    | 'answerMode' | 'acceptedAnswers' | 'matchingOptions' | 'maxWords'
    | 'rubric'
    | 'passageText' | 'passageTitle'
    | 'topic'
  >
> & { mediaUrl?: string; aiImageDescription?: string; subQuestions?: unknown[] };

export interface CreateBankQuestionData extends BankQuestionTypeFields {
  subjectId: string;
  type: string;
  prompt: string;
  options?: Array<{ id: string; value: string }>;
  matchPairs?: Array<{ key: string; value: string }>;
  correctAnswer: string | string[] | Record<string, string>;
  points?: number;
  explanation?: string;
  difficulty?: 'easy' | 'medium' | 'hard';
}

export interface ListBankQuestionsParams {
  subjectId?: string;
  difficulty?: 'easy' | 'medium' | 'hard';
  /** Question-type filter (e.g. MULTIPLE_CHOICE) — matches the stored type. */
  type?: string;
  search?: string;
  /** Provenance filter — matches questions banked from a given quiz draft. */
  sourceArtifactId?: string;
  page?: number;
  limit?: number;
}

export interface PaginatedBankQuestions {
  data: BankQuestion[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export const List = (
  params?: ListBankQuestionsParams,
): AxiosPromise<{ success: boolean } & PaginatedBankQuestions> =>
  http.request.get('/api/question-bank', { params });

export const GetById = (id: string): AxiosPromise<{ success: boolean; data: BankQuestion }> =>
  http.request.get(`/api/question-bank/${id}`);

export const Create = (
  data: CreateBankQuestionData,
): AxiosPromise<{ success: boolean; data: BankQuestion }> =>
  http.request.post('/api/question-bank', data);

export const BulkCreate = (
  items: CreateBankQuestionData[],
): AxiosPromise<{ success: boolean; data: { created: number } }> =>
  http.request.post('/api/question-bank/bulk', { items });

export const Update = (
  id: string,
  data: Partial<CreateBankQuestionData>,
): AxiosPromise<{ success: boolean; data: BankQuestion }> =>
  http.request.patch(`/api/question-bank/${id}`, data);

/** The org's existing question topics, most used first (scoped by X-Organization-Id). */
export const ListTopics = (): AxiosPromise<{ success: boolean; data: string[] }> =>
  http.request.get('/api/question-topics');

export const Delete = (id: string): AxiosPromise<{ success: boolean }> =>
  http.request.delete(`/api/question-bank/${id}`);

export const BulkDelete = (
  ids: string[],
): AxiosPromise<{ success: boolean; data: { deleted: number } }> =>
  http.request.post('/api/question-bank/bulk-delete', { ids });

// ─── File import ─────────────────────────────────────────────────────────────

/** Machine code + English fallback; the client localizes on `code` when it knows it. */
export interface ImportIssue {
  code: string;
  message: string;
  /** How many rows a notice is about (`ai_repair_applied`), for the localized text. */
  count?: number;
}

/** Why the AI replaced a row the parser had read. */
export type ImportRepairReason = 'shared_options' | 'multi_part' | 'type_corrected' | 'answer_copied' | 'fields_fixed';

type ImportedQuestion = CreateBankQuestionData & { subjectName?: string };

export interface ImportRow {
  /** Source position — spreadsheet row number, or question index for text/JSON files. */
  rowNumber: number;
  label: string;
  errors: ImportIssue[];
  warnings: ImportIssue[];
  /** Null when the row could not be parsed; `errors` says why. */
  question: ImportedQuestion | null;
  /** Present when the AI replaced this row; `original` is what the parser read (null: nothing usable). */
  repair?: { original: ImportedQuestion | null; reason: ImportRepairReason };
}

export interface ImportPreviewResult {
  format: 'xlsx' | 'csv' | 'json' | 'text' | 'pdf' | 'docx';
  rows: ImportRow[];
  summary: { total: number; valid: number; invalid: number; truncated: boolean };
  notices: ImportIssue[];
  unknownSubjects: string[];
}

/** Parses the file server-side; nothing is written until the rows are sent to BulkCreate. */
export const PreviewImport = (
  file: File,
  subjectId?: string,
  options: { aiRepair?: boolean; signal?: AbortSignal } = {},
): AxiosPromise<{ success: boolean; data: ImportPreviewResult }> => {
  const formData = new FormData();
  formData.append('file', file);
  if (subjectId) formData.append('subjectId', subjectId);
  // Sent only when on, so a request without the AI pass is exactly what it was before.
  if (options.aiRepair) formData.append('aiRepair', 'true');
  // Content-Type is left to Axios so the multipart boundary is included.
  return http.request.post('/api/question-bank/import/preview', formData, { signal: options.signal });
};

/** Whether the platform allows the AI pass at all; the plan is checked separately with `useFeature`. */
export const GetImportAiRepairStatus = (): AxiosPromise<{ success: boolean; data: { enabled: boolean } }> =>
  http.request.get('/api/question-bank/import/ai-repair');

export const DownloadImportTemplate = (format: 'xlsx' | 'csv' | 'txt' | 'json' | 'docx' | 'pdf'): AxiosPromise<Blob> =>
  http.request.get(`/api/question-bank/import/template?format=${format}`, {
    responseType: 'blob',
  });
